import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { creates, hasEntityAccess, managesConfigArea, managesPermissions, type AuthzCheck } from './authzChecks'
import { all, always, any, ldFlag, omneanOnly, orgFlag, type AvailabilityRule } from './availability'
import {
  adminsOnly,
  adminsOrAssigneeRoles,
  everyRole,
  everyRoleExceptSuppliers,
  nonViewerAdmins,
  superAdmin,
  superAdminOrP2pAdmin,
  type RoleAccessRule,
} from './roleAccess'
import type { ConfigArea, PermissionSetEntity } from '@omnea-app/authz'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'

/** `open` still applies availability first. */
export type AccessRule =
  | { kind: 'open' }
  | { kind: 'fga'; check: AuthzCheck }
  | { kind: 'fgaOrRole'; check: AuthzCheck; role: RoleAccessRule }
  | { kind: 'role'; role: RoleAccessRule }

/**
 * Which query parameter distinguishes routes sharing one pattern, and the value that selects this
 * route.
 */
export type RouteSegment = { queryParam: string; value: string }

type RouteAddress = {
  /**
   * The Next.js route pattern, as `router.route` reports it. Hand-maintained strings the compiler
   * cannot check against the pages directory: a pattern matching no real page makes its route
   * unresolvable, and so ungated.
   */
  pattern: string
  segment?: RouteSegment
}

/** What reaching a route requires, shared by the routes that require the same thing. */
type RouteRule = {
  availability: AvailabilityRule
  access: AccessRule
  deniedRedirectTo?: DeniedRedirectKey
}

/**
 * A denial's destination has to satisfy two things at once: it must admit every caller, or a denial
 * bounces between two routes forever, and it must be reachable without caller-supplied values, or
 * there is no URL to send them to.
 */
type DeniedRedirectKey = 'dashboard' | 'settingsNotifications'

/** A route that shows one record carries its id as `[id]`, which the route gate checks before the page. */
type RecordRoutePattern = `${string}/[id]` | `${string}/[id]/${string}`

/** How a route is addressed as a URL, where its pattern does not already say. */
type RouteNavigation = { params?: undefined; href?: string } | { params: readonly [string, ...string[]]; href?: undefined }

export type RouteDefinition =
  | (RouteAddress & RouteRule & RouteNavigation & { record?: undefined })
  | (RouteAddress &
      RouteRule & {
        pattern: RecordRoutePattern
        record: PermissionSetEntity
        params: readonly [string, ...string[]]
        href?: undefined
      })

const SETTINGS_FALLBACK_ROUTE = 'settingsNotifications'

export const DEFAULT_DENIED_REDIRECT = 'dashboard'

/**
 * Most settings pages share one page file and are told apart by their mode segment. The exceptions are
 * the ones with a page file of their own: Next routes a concrete path ahead of the optional catch-all,
 * so a mode segment for those would match nothing and leave the route unresolvable — hence ungated.
 */
const SETTINGS_PATTERN = '/settings/[[...mode]]'

const modeSegment = (value: string): RouteSegment => ({ queryParam: 'mode', value })

/**
 * What a settings route requires: the configuration area it belongs to, answered by the service where
 * it can and by the caller's role otherwise.
 *
 * TODO(Permissions): the service arm deliberately omits the role dimension, so a configuration-area
 * grant alone admits a non-admin. Their writes will still fail — the backend derives a caller's
 * permission role from session claims alone, so no grant lifts them to `admin`, and most settings
 * endpoints exclude the `user` role. Those endpoints MUST authorize on the service before enforcement is
 * rolled out beyond the pilot organisations.
 */
const configGated = (area: ConfigArea, role: RoleAccessRule = superAdmin): AccessRule => ({
  kind: 'fgaOrRole',
  check: managesConfigArea(area),
  role,
})

/**
 * The rollout is folded into the answer rather than named here: `canManagePermissions` reads false until
 * RELEASE_FGA_CONFIG names the caller, and the enforcement flag plays no part, so permissions can be
 * configured before the organisation enforces them. A denial redirects rather than reporting the page
 * non-existent.
 */
const PERMISSIONS_ROUTE = {
  availability: always(),
  access: { kind: 'fga', check: managesPermissions },
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const REPORTING_ROUTE = {
  availability: always(),
  access: { kind: 'role', role: nonViewerAdmins },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteRule

/** Conversational intake ships to Omneans ahead of the organisations the chat assistant is sold to. */
const AI_ROUTE = {
  availability: any([
    orgFlag(FeatureFlagsEnum.AI_CHAT_ASSISTANT),
    all([orgFlag(FeatureFlagsEnum.OMNEANS_ONLY_CONV_INTAKE), omneanOnly()]),
  ]),
  access: { kind: 'open' },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteRule

/**
 * Each of these is shared by a landing route and the detail pages beneath it, which must answer alike:
 * a nested page left on its own rule stays reachable under terms its parent has stopped allowing.
 */
const AI_BULK_EXTRACTION_ROUTE = {
  availability: ldFlag('RELEASE_AI_BULK_EXTRACTION'),
  access: configGated('organisation_settings'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const DOCUMENT_STORAGE_ROUTE = {
  availability: orgFlag(FeatureFlagsEnum.ENABLE_DOCUMENT_EXTRACTION),
  access: configGated('document_review'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const ENVIRONMENT_SYNC_ROUTE = {
  availability: ldFlag('RELEASE_CONFIG_SYNC'),
  access: { kind: 'fgaOrRole', check: managesConfigArea('workflows_and_forms'), role: superAdmin },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteRule

const INTEGRATIONS_ROUTE = {
  availability: always(),
  access: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const PUBLIC_API_ROUTE = {
  availability: all([orgFlag(FeatureFlagsEnum.PUBLIC_API_UI), orgFlag(FeatureFlagsEnum.PUBLIC_API)]),
  access: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

const INTEGRATION_BUILDER_ROUTE = {
  availability: ldFlag('RELEASE_INTEGRATION_BUILDER_V2'),
  access: configGated('it'),
  deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
} as const satisfies RouteRule

/** Shared with the `/workflows` routes, which mount the same pages outside the settings tree. */
const WORKFLOW_EDITOR_ROUTE = {
  availability: always(),
  access: configGated('workflows_and_forms', superAdminOrP2pAdmin),
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteRule

/**
 * Starting a request, drafts included: a draft only resumes into creating one. Shared with the
 * new-request and Drafts links, which must agree with the route they lead
 * to. Before enforcement every app role could start one, so the role arm keeps that.
 */
export const CREATE_REQUEST_ROUTE = {
  availability: always(),
  access: { kind: 'fgaOrRole', check: creates('request'), role: everyRoleExceptSuppliers },
  deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
} as const satisfies RouteRule

// Role arms keep pre-FGA behaviour: the suppliers list was super-admin only, the form picker open to every role.
export const CREATE_SUPPLIER_ROUTE = {
  availability: always(),
  access: { kind: 'fgaOrRole', check: creates('supplier'), role: superAdmin },
} as const satisfies RouteRule

export const CREATE_SUPPLIER_IN_FORM_ROUTE = {
  availability: always(),
  access: { kind: 'fgaOrRole', check: creates('supplier'), role: everyRole },
} as const satisfies RouteRule

/** Keyed by entity so a new permission-set family cannot ship with its list route ungated. */
const ENTITY_LIST_ROUTES = {
  request: {
    availability: always(),
    access: { kind: 'fgaOrRole', check: hasEntityAccess('request'), role: everyRoleExceptSuppliers },
  },
  supplier: {
    availability: always(),
    access: { kind: 'fgaOrRole', check: hasEntityAccess('supplier'), role: adminsOrAssigneeRoles },
  },
  agreement: {
    availability: orgFlag(FeatureFlagsEnum.CREATE_AGREEMENT),
    access: { kind: 'fgaOrRole', check: hasEntityAccess('agreement'), role: everyRole },
  },
} as const satisfies Readonly<Record<PermissionSetEntity, RouteRule>>

/**
 * A detail route answers as its list does, then the route gate holds the page until the record's own
 * check allows it.
 */
const detailRouteFor = <Pattern extends RecordRoutePattern, Params extends readonly [string, ...string[]]>(
  entity: PermissionSetEntity,
  pattern: Pattern,
  params: Params
) =>
  ({
    ...ENTITY_LIST_ROUTES[entity],
    record: entity,
    pattern,
    params,
  }) as const

export const ROUTE_REGISTRY = {
  dashboard: { pattern: '/', availability: always(), access: { kind: 'open' } },
  tasks: { pattern: '/tasks/[[...mode]]', availability: always(), access: { kind: 'open' } },
  startRequest: { ...CREATE_REQUEST_ROUTE, pattern: '/start' },
  startDrafts: { ...CREATE_REQUEST_ROUTE, pattern: '/start/drafts' },
  ai: { ...AI_ROUTE, pattern: '/ai' },
  aiChat: { ...AI_ROUTE, pattern: '/ai/[mode]', params: ['mode'] },
  requestsList: { ...ENTITY_LIST_ROUTES.request, pattern: '/requests/[[...mode]]' },
  suppliersList: { ...ENTITY_LIST_ROUTES.supplier, pattern: '/suppliers/[[...mode]]' },
  agreementsList: { ...ENTITY_LIST_ROUTES.agreement, pattern: '/agreements/[[...mode]]' },
  agreementDetailIndex: detailRouteFor('agreement', '/agreements/detail/[id]', ['id']),
  agreementDetail: detailRouteFor('agreement', '/agreements/detail/[id]/[tab]', ['id', 'tab']),
  agreementDetailSubmission: detailRouteFor('agreement', '/agreements/detail/[id]/[tab]/[submissionId]', [
    'id',
    'tab',
    'submissionId',
  ]),
  agreementAuditLog: detailRouteFor('agreement', '/agreements/detail/[id]/audit-log', ['id']),
  assessmentsList: {
    pattern: '/assessments/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.ENABLE_ASSESSMENTS),
    access: { kind: 'role', role: adminsOrAssigneeRoles },
  },
  renewalsList: {
    pattern: '/renewals/[[...mode]]',
    availability: always(),
    access: { kind: 'role', role: adminsOrAssigneeRoles },
  },
  documentsList: {
    pattern: '/documents/[[...mode]]',
    availability: always(),
    access: { kind: 'role', role: adminsOrAssigneeRoles },
  },
  purchaseOrdersList: {
    pattern: '/purchase-orders/[[...tab]]',
    availability: ldFlag('RELEASE_PURCHASE_ORDER_UPDATES'),
    access: { kind: 'role', role: adminsOnly },
  },
  scheduledRequestsList: {
    pattern: '/scheduled-requests/[[...mode]]',
    availability: ldFlag('RELEASE_SCHEDULED_REQUESTS'),
    access: { kind: 'role', role: nonViewerAdmins },
  },
  budgetsList: {
    pattern: '/budgets/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.BUDGETS),
    access: { kind: 'role', role: adminsOnly },
  },
  billsList: {
    pattern: '/bills/[[...tab]]',
    availability: orgFlag(FeatureFlagsEnum.INVOICE_PROCESSING),
    access: { kind: 'role', role: adminsOnly },
  },
  productCatalogues: {
    pattern: '/products-and-services',
    availability: any([
      all([orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES), ldFlag('SHOW_PUNCHOUT_CATALOGUES')]),
      orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    ]),
    access: { kind: 'open' },
  },
  savingsDashboard: {
    pattern: '/sourcing/[[...mode]]',
    availability: ldFlag('RELEASE_S_AND_P_SAVINGS_DASHBOARD'),
    access: { kind: 'open' },
  },
  risksList: {
    pattern: '/risks/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    access: { kind: 'open' },
  },
  riskAlertsList: {
    pattern: '/risk-alerts/[[...mode]]',
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), orgFlag(FeatureFlagsEnum.TPRM_CONTINUOUS_MONITORING)]),
    access: { kind: 'open' },
  },
  subprocessorsList: {
    pattern: '/subprocessors/[[...mode]]',
    availability: orgFlag(FeatureFlagsEnum.SUPPLIER_SUBPROCESSORS),
    access: { kind: 'role', role: adminsOnly },
  },
  // Insights and its legacy `/reporting` aliases follow the side menu, which has hidden them from
  // view-only admins throughout.
  //
  // Insights opens on Custom Dashboards; linking to the bare `/insights` costs a client redirect.
  insights: { ...REPORTING_ROUTE, pattern: '/insights/[[...path]]', href: '/insights/custom-dashboards' },
  reporting: { ...REPORTING_ROUTE, pattern: '/reporting/[[...path]]' },
  reportingDataDestinations: {
    pattern: '/reporting/data-destinations',
    availability: ldFlag('RELEASE_REPORTING_CLOUD_DELIVERY_UI_V2'),
    access: { kind: 'role', role: superAdmin },
    deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
  },
  workflowsList: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows' },
  workflowAuditLog: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]/audit-log', params: ['id'] },
  workflowDraftEdit: {
    ...WORKFLOW_EDITOR_ROUTE,
    pattern: '/workflows/edit/[id]/drafts/[draftId]',
    params: ['id', 'draftId'],
  },
  workflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]', params: ['id'] },
  settingsPermissions: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions' },
  settingsPermissionsConfigAccess: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/config-access' },
  settingsPermissionsRoles: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/roles' },
  settingsPermissionsPermissionSets: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/permission-sets' },
  settingsNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('notifications'),
    availability: always(),
    access: { kind: 'open' },
  },
  // Both follow the side menu rather than the page: reaching a settings route requires the config area
  // its menu section requires.
  settingsAgentLibrary: {
    pattern: '/settings/agent-library',
    availability: ldFlag('RELEASE_AGENT_MARKETPLACE_V1'),
    access: { kind: 'fgaOrRole', check: managesConfigArea('workflows_and_forms'), role: superAdmin },
    deniedRedirectTo: DEFAULT_DENIED_REDIRECT,
  },
  settingsEnvironmentSync: { ...ENVIRONMENT_SYNC_ROUTE, pattern: '/settings/environment-sync' },
  settingsEnvironmentSyncJob: {
    ...ENVIRONMENT_SYNC_ROUTE,
    pattern: '/settings/environment-sync/[workflowId]/[jobId]',
    params: ['workflowId', 'jobId'],
  },
  settingsAdmin: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('admin'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigration: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationConfigureMappings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-configure-mappings'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateEngagements: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-engagements'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateInFlight: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-in-flight'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationMigrateRenewals: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-renewals'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAgreementsMigrationVerifyInFlight: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-verify-in-flight'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAi: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAuctions: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auctions'),
    availability: orgFlag(FeatureFlagsEnum.AUCTIONS),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAiBulkExtraction: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai-bulk-extraction'),
  },
  settingsAiBulkExtractionJob: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: '/settings/ai-bulk-extraction/[jobId]',
    params: ['jobId'],
  },
  settingsAiBulkExtractionJobReview: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: '/settings/ai-bulk-extraction/[jobId]/review',
    params: ['jobId'],
  },
  settingsApi: { ...PUBLIC_API_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('api') },
  settingsApiOAuthApp: { ...PUBLIC_API_ROUTE, pattern: '/settings/api/oauth/[integrationId]', params: ['integrationId'] },
  settingsApiOAuthEntities: {
    ...PUBLIC_API_ROUTE,
    pattern: '/settings/api/oauth/[integrationId]/entities',
    params: ['integrationId'],
  },
  settingsApiOAuthEntityFields: {
    ...PUBLIC_API_ROUTE,
    pattern: '/settings/api/oauth/[integrationId]/fields/[entityType]',
    params: ['integrationId', 'entityType'],
  },
  settingsAuditLog: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('audit-log'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  // Reached only from a card on the Import page, so it answers to the area that governs Import.
  settingsBulkDocumentsUpload: {
    pattern: '/settings/bulk-documents-upload',
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsExternalArchive: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('external-archive'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsAutoFlaggedCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auto-flagged-categories'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('catalogues'),
    availability: any([
      orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
      orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES),
      orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    ]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDynamicAssignees: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('dynamic-assignees'),
    availability: always(),
    access: configGated('dynamic_assignees'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCoreData: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('core-data'),
    availability: always(),
    access: configGated('core_data'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDefaultNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('default-notifications'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsDocumentStorage: { ...DOCUMENT_STORAGE_ROUTE, pattern: '/settings/document-storage' },
  settingsDocumentStorageType: {
    ...DOCUMENT_STORAGE_ROUTE,
    pattern: '/settings/document-storage/types/[id]',
    params: ['id'],
  },
  settingsInternalFeatureFlags: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('__feature-flags'),
    availability: omneanOnly(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsForms: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('forms'),
    availability: always(),
    access: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsImport: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('import'),
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsIntegrations: { ...INTEGRATIONS_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('integrations') },
  settingsIntegrationEntities: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/entities',
    params: ['integrationId'],
  },
  settingsIntegrationEntityFields: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/fields/[entityType]',
    params: ['integrationId', 'entityType'],
  },
  settingsIntegrationFilters: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/filters',
    params: ['integrationId'],
  },
  settingsIntegrationPermissions: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/permissions',
    params: ['integrationId'],
  },
  settingsIntegrationProcedures: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures',
    params: ['integrationId'],
  },
  settingsIntegrationProcedureTemplate: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures/[templateId]',
    params: ['integrationId', 'templateId'],
  },
  settingsIntegrationVariables: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/variables',
    params: ['integrationId'],
  },
  settingsLocalisation: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('localisation'),
    availability: orgFlag(FeatureFlagsEnum.CONFIG_TRANSLATIONS),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsPolicies: {
    pattern: '/settings/policies',
    availability: always(),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  // A policy is shared by link, so its own page stays reachable by anyone the documents service will
  // answer for; the store that lists every policy does not.
  settingsPolicy: { pattern: '/settings/policies/[id]', params: ['id'], availability: always(), access: { kind: 'open' } },
  settingsPreferences: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('preferences'),
    availability: always(),
    access: { kind: 'open' },
  },
  settingsProductCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('product-catalogue'),
    availability: orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsPunchoutCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('punchout-catalogues'),
    availability: all([omneanOnly(), orgFlag(FeatureFlagsEnum.PUNCHOUT_CATALOGUES), ldFlag('SHOW_PUNCHOUT_CATALOGUES')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsHostedCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('hosted-catalogues'),
    availability: orgFlag(FeatureFlagsEnum.HOSTED_CATALOGUES),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('categories'),
    availability: any([
      orgFlag(FeatureFlagsEnum.PRODUCTS_AND_SERVICES_CATALOGUE),
      orgFlag(FeatureFlagsEnum.SUPPLIER_CATEGORISATION),
    ]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRisks: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risks'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRiskManagement: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-management'),
    availability: orgFlag(FeatureFlagsEnum.TPRM),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsRiskAlerts: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-alerts'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), orgFlag(FeatureFlagsEnum.TPRM_CONTINUOUS_MONITORING)]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSubprocessorSettings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('subprocessor-settings'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), orgFlag(FeatureFlagsEnum.SUPPLIER_SUBPROCESSORS)]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-catalogue'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-groups'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsArtefactsLibrary: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefacts-library'),
    availability: all([orgFlag(FeatureFlagsEnum.TPRM), ldFlag('RELEASE_ARTEFACTS_AGENT')]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsGoodsReceipting: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('goods-receipting'),
    availability: orgFlag(FeatureFlagsEnum.GOODS_RECEIPTING),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsInvoiceProcessing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('invoice-processing'),
    availability: orgFlag(FeatureFlagsEnum.INVOICE_PROCESSING),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSecurity: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('security'),
    availability: always(),
    access: configGated('it'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSourcing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing'),
    availability: orgFlag(FeatureFlagsEnum.RFX),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsSourcingMemory: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing-memory'),
    availability: all([omneanOnly(), orgFlag(FeatureFlagsEnum.RFX)]),
    access: configGated('organisation_settings'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsUserProperties: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('user-properties'),
    availability: always(),
    access: configGated('it'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsUsersAndGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('users-and-groups'),
    availability: always(),
    access: configGated('users_and_groups'),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsWorkflows: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('workflows'),
    availability: always(),
    access: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    deniedRedirectTo: SETTINGS_FALLBACK_ROUTE,
  },
  settingsWorkflowDraftEdit: {
    ...WORKFLOW_EDITOR_ROUTE,
    pattern: '/settings/workflows/edit/[id]/drafts/[draftId]',
    params: ['id', 'draftId'],
  },
  settingsWorkflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/settings/workflows/edit/[id]', params: ['id'] },
} as const satisfies Readonly<Record<RouteKey, RouteDefinition>>

// `Object.entries` widens the key to `string`; `ROUTE_REGISTRY` is `Record<RouteKey, …>`, so every key is a `RouteKey`.
export const ROUTE_REGISTRY_ENTRIES = Object.entries(ROUTE_REGISTRY) as readonly [RouteKey, RouteDefinition][]
